/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   JobDashboard.tsx
 *     Feature         :   114-job-infrastructure
 *     Description     :   Job Dashboard page for viewing job status and history
 *     Constitution    :   v1.11.0 | Principle XXXVIII (Specification Testing Standards)
 *     Author          :   Peter Dobler
 *     Created         :   2026-01-28
 *     Modified        :   2026-06-14 | Conformed to Spec 197 design blueprint — migrated
 *                         off MUI (Box/Grid/Paper/Card/Tabs/Chip/Table via JobList/Alert/
 *                         Snackbar) onto the canonical Tailwind+token UI kit (PageLayout /
 *                         Card / DataTable / StatCard / Button / Badge / states). The
 *                         Recent Runs table is now a <DataTable> rendered inline; server
 *                         pagination is preserved with a kit-Button pager. Presentation
 *                         only — data fetching, polling, filters, and all handlers are
 *                         unchanged. Child dialogs (TriggerJobDialog / JobDetail) keep
 *                         their own modals.
 * =========================================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Play,
  CheckCircle,
  XCircle,
  Hourglass,
  Eye,
  X,
  AlertTriangle,
} from 'lucide-react';

import TriggerJobDialog from './TriggerJobDialog';
import { RunNowButton } from './RunNowButton';
import { markRunRequested, useTenantJobSchedule } from './useTenantJobSchedule';
import JobDetail from './JobDetail';
import JobSchedules from './JobSchedules';
import BenchmarksPanel from './BenchmarksPanel';
import {
  getJobDashboardSummary,
  getJobRuns,
  getJobDefinitions,
  cancelJobRun,
  canCancelJobRun,
  getStatusLabel,
  getStatusColor,
  formatDuration,
  formatRelativeTime,
  JobDashboardSummary,
  JobRun,
  JobDefinition,
  JobRunFilters,
  JobRunStatus,
  getJobTypeLabel,
} from '../../services/jobService';
import {
  PageLayout,
  Card,
  DataTable,
  Button,
  Badge,
  StatCard,
  ErrorState,
  LoadingState,
  Extended,
  QuickGlance,
  AppTooltip,
  PageHelpPanel,
  PaginationControls,
  PAGE_SIZES,
} from '../../components/ui';
import type { DataTableColumn } from '../../components/ui';
// Spec 265 US5 — collection integrity. THIS is the routed Collection Jobs screen
// (/jobs); pages/operations/CollectionJobsPage.tsx is a separate legacy screen at
// /jobs/collection-jobs. The panel was first wired to that one and was therefore
// invisible — the duplicate-page trap Spec 216 documented.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CollectionIntegrityPanel } from '../../components/operations/CollectionIntegrityPanel';
import { collectionIntegrityApi } from '../../services/collectionIntegrityApi';
import { jobsHelp } from '../../data/help/jobsHelp';
import { formatEasternTime } from '@/utils/dateFormatter';

// Kit <Badge> variants used by the status mapping below.
type BadgeVariant = 'success' | 'error' | 'warning' | 'info' | 'secondary';

// Polling interval in milliseconds (60 seconds per FR-114)
const POLL_INTERVAL = 60_000;

// Tabs for the dashboard body (token underline tabs replace MUI <Tabs>).
const TABS = ['Recent Runs', 'Job Definitions', 'Schedules'] as const;

// Map the service status-color name to a kit <Badge> variant (presentation only —
// replaces the MUI-based JobStatusBadge inline so the runs table uses kit components).
const STATUS_BADGE_VARIANT: Record<
  ReturnType<typeof getStatusColor>,
  BadgeVariant
> = {
  success: 'success',
  error: 'error',
  warning: 'warning',
  info: 'info',
  default: 'secondary',
};

export default function JobDashboard() {
  // Read-only; a failure here degrades this panel alone and never blanks the page.
  const queryClient = useQueryClient();
  const jobStates = useTenantJobSchedule().data?.job_states ?? {};
  const integrity = useQuery({
    queryKey: ['collection-integrity', 'collection-jobs'],
    queryFn: () => collectionIntegrityApi.getCollectionJobs(),
    refetchInterval: 60_000,
  });
  // State
  const [summary, setSummary] = useState<JobDashboardSummary | null>(null);
  const [jobRuns, setJobRuns] = useState<JobRun[]>([]);
  const [definitions, setDefinitions] = useState<JobDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  // 🛑 MUST be one of PAGE_SIZES ([10, 25, 50, 100]). The old server default was 20,
  //    which the selector cannot represent: with no matching <option> the <select>
  //    falls back to displaying the FIRST one, so the control read "10" while the
  //    server paged at 20 and the footer said "1-20". A control showing a number it
  //    does not honour - the exact defect this page was being fixed for, reintroduced
  //    by keeping 20 as the default while wiring a selector that has no 20.
  const [filters, setFilters] = useState<JobRunFilters>({
    page: 1,
    page_size: PAGE_SIZES[0],
  });
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Trigger dialog state
  const [triggerDialogOpen, setTriggerDialogOpen] = useState(false);
  const [selectedDefinition, setSelectedDefinition] = useState<JobDefinition | null>(null);
  const [successSnackbar, setSuccessSnackbar] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Job detail modal state
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedJobRunId, setSelectedJobRunId] = useState<string | null>(null);

  // Fetch data
  const fetchData = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      setError(null);

      const [summaryData, runsData, defsData] = await Promise.all([
        getJobDashboardSummary(),
        getJobRuns(filters),
        getJobDefinitions(),
      ]);

      setSummary(summaryData);
      setJobRuns(runsData.data);
      setTotalPages(runsData.pagination.total_pages);
      setTotalItems(runsData.pagination.total_items);
      setDefinitions(defsData);
      setLastRefresh(new Date());
    } catch (err: any) {
      console.error('Failed to fetch job data:', err);
      setError(err.message || 'Failed to load job data');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Initial load and polling
  useEffect(() => {
    fetchData();

    // Set up polling (FR-114: 60-second refresh)
    const pollInterval = setInterval(() => {
      fetchData(false); // Don't show loading spinner for background refresh
    }, POLL_INTERVAL);

    return () => clearInterval(pollInterval);
  }, [fetchData]);

  // Handle filter change
  const handleFilterChange = (newFilters: Partial<JobRunFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters, page: 1 }));
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  // 🛑 The page-size selector drives the SERVER's page_size, which is the whole point.
  //    It was previously suppressed (showPagination={false}) because the table's own
  //    selector paged client-side over a server-paged slice: it displayed "10" while 20
  //    rows rendered, and changing it could not affect the query. A control that shows a
  //    number it does not honour is worse than no control. Wiring it to the request makes
  //    it true. Reset to page 1 - staying on page 12 of a 20-row paging while switching
  //    to 100 rows would silently land somewhere unrelated.
  const handlePageSizeChange = (size: number) => {
    setFilters(prev => ({ ...prev, page_size: size, page: 1 }));
  };

  // Handle status filter
  const handleStatusFilter = (status: JobRunStatus | undefined) => {
    handleFilterChange({ status });
  };

  // Handle trigger job
  const handleTriggerClick = (definition: JobDefinition) => {
    setSelectedDefinition(definition);
    setTriggerDialogOpen(true);
  };

  const handleTriggerSuccess = (message?: string) => {
    if (selectedDefinition) markRunRequested(queryClient, selectedDefinition.name);
    setSuccessMessage(message ?? 'Job started. Check Recent Runs for status.');
    setSuccessSnackbar(true);
    // A collection has no run row until the worker starts it (only those answer with a message), so stay on the
    // card, which shows what it is doing. A merge has its row at once, under Recent Runs.
    if (!message) setTabValue(0);
    fetchData();
  };

  const handleCancelRun = async (jobRun: JobRun) => {
    try {
      await cancelJobRun(jobRun.id);
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to cancel job run');
    }
  };

  // Handle view job details
  const handleViewJobDetails = (jobRunId: string) => {
    setSelectedJobRunId(jobRunId);
    setDetailDialogOpen(true);
  };

  // Auto-dismiss the success toast (replaces MUI <Snackbar autoHideDuration>).
  useEffect(() => {
    if (!successSnackbar) return;
    const t = setTimeout(() => setSuccessSnackbar(false), 4000);
    return () => clearTimeout(t);
  }, [successSnackbar]);

  // Recent-runs table columns (inlined from the legacy JobList markup).
  // 🛑 NO per-column `width` percentages. They forced a fixed layout inside the
  //    `minWidth="52rem"` frame, so `Duration` got ~10% of 52rem and its header rendered
  //    as "Durati…" — a clipped column heading on the one table that had explicitly
  //    sized itself. Every other table on this screen sets no widths and reads correctly;
  //    the blueprint's sizing lever is `minWidth` (below), not per-column percentages.
  const runColumns: DataTableColumn<JobRun>[] = [
    {
      key: 'name',
      header: 'Job Name',
      render: (job) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-[var(--color-text-primary)]">
            {job.job_definition?.name || 'Unknown Job'}
          </div>
          <div className="truncate text-xs text-[var(--color-text-secondary)]">
            {job.run_id.slice(0, 8)}...
          </div>
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      render: (job) =>
        job.job_definition?.job_type
          ? getJobTypeLabel(job.job_definition.job_type as any)
          : '-',
    },
    {
      key: 'status',
      header: 'Status',
      render: (job) => (
        <Badge variant={STATUS_BADGE_VARIANT[getStatusColor(job.status)]}>
          {getStatusLabel(job.status)}
        </Badge>
      ),
    },
    {
      key: 'started',
      header: 'Started',
      render: (job) => (
        <div className="min-w-0">
          <div className="text-[var(--color-text-primary)]">
            {formatRelativeTime(job.started_at || job.created_at)}
          </div>
          {job.started_at && (
            <div className="text-xs text-[var(--color-text-secondary)]">
              {formatEasternTime(job.started_at)}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'duration',
      header: 'Duration',
      render: (job) => formatDuration(job.duration_seconds),
    },
    {
      key: 'triggered_by',
      header: 'Triggered By',
      render: (job) => (
        <div className="min-w-0">
          <div className="truncate text-[var(--color-text-primary)]">{job.triggered_by || '-'}</div>
          <div className="truncate text-xs text-[var(--color-text-secondary)]">{job.trigger_type}</div>
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (job) => (
        <div className="flex items-center justify-end gap-1">
          <AppTooltip title="View Details">
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                handleViewJobDetails(job.id);
              }}
            >
              <Eye className="h-4 w-4" />
            </Button>
          </AppTooltip>
          {canCancelJobRun(job) && (
            <AppTooltip title="Cancel Job">
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancelRun(job);
                }}
              >
                <XCircle className="h-4 w-4 text-[var(--color-status-danger)]" />
              </Button>
            </AppTooltip>
          )}
        </div>
      ),
    },
  ];

  // Status filter options (token tabs of pill buttons replace MUI filter <Chip>s).
  const statusFilters: { label: string; value: JobRunStatus | undefined }[] = [
    { label: 'All', value: undefined },
    { label: 'Running', value: 'RUNNING' },
    { label: 'Completed', value: 'COMPLETED' },
    { label: 'Failed', value: 'FAILED' },
    { label: 'Pending', value: 'PENDING' },
  ];

  const page = filters.page || 1;

  // Spec 285 / 226: the standard Refresh reloads the summary/runs/definitions, the integrity panel
  // and the benchmarks. (The Schedules tab loads on mount and exposes no handle — reopen the tab.)
  const refreshAll = async (): Promise<void> => {
    await Promise.all([
      fetchData(),
      integrity.refetch(),
      queryClient.invalidateQueries({ queryKey: ['collection-jobs', 'benchmarks'] }),
    ]);
  };

  if (loading && !summary) {
    return (
      <PageLayout title="Collection Jobs" icon={<Play className="h-6 w-6" />}>
        <Card>
          <LoadingState rows={6} />
        </Card>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Collection Jobs"
      description="Run and monitor your organization's collection jobs, and see their schedule"
      icon={<Play className="h-6 w-6" />}
      actions={
        <>
          {/* The collection job's own run log (fcjlogs): a route that had no way in until 2026-11-01. */}
          <Link to="/operations/collection-job-logs" className="text-sm text-[var(--color-accent)] hover:underline">
            Collection job logs
          </Link>
          <PageHelpPanel content={jobsHelp} buttonSize="sm" />
          <span className="text-sm text-[var(--color-text-secondary)]">
            Last updated: {formatEasternTime(lastRefresh)}
          </span>
        </>
      }
      onRefresh={refreshAll}
      isRefreshing={loading}
    >
      {/* Spec 265 US5 — what each collector actually captured, and what it could not
          see. Rendered only once loaded: an empty panel would assert health it has not
          measured, which is the failure mode this feature exists to remove. */}
      {integrity.data && (
        <div className="mb-6">
          <CollectionIntegrityPanel
            completeness={integrity.data.completeness}
            problems={integrity.data.problems}
            problemCounts={integrity.data.problemCounts}
            streaks={integrity.data.streaks}
          />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mb-6">
          <ErrorState title="Failed to load job data" message={error} onRetry={() => fetchData()} />
        </div>
      )}

      {/* Summary tiles */}
      {summary && (
        <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <AppTooltip title="Number of jobs currently in progress" className="block">
            <StatCard
              label="Running Jobs"
              value={summary.running_count}
              valueClassName="text-[var(--color-status-info)]"
              icon={<Play className="h-4 w-4" />}
            />
          </AppTooltip>
          <StatCard
            label="Completed (24h)"
            value={summary.completed_count}
            valueClassName="text-[var(--color-status-success)]"
            icon={<CheckCircle className="h-4 w-4" />}
          />
          <StatCard
            label="Failed (24h)"
            value={summary.failed_count}
            valueClassName="text-[var(--color-status-danger)]"
            icon={<XCircle className="h-4 w-4" />}
          />
          <StatCard
            label="Success Rate"
            // Spec 298 FR-004: no finished run in the window has no rate; it says so, never "0%" in red
            value={summary.success_rate === null ? 'no runs' : `${summary.success_rate}%`}
            valueClassName={
              summary.success_rate === null
                ? undefined
                : summary.success_rate >= 90
                ? 'text-[var(--color-status-success)]'
                : summary.success_rate >= 70
                ? 'text-[var(--color-status-warning)]'
                : 'text-[var(--color-status-danger)]'
            }
            icon={<Hourglass className="h-4 w-4" />}
          />
        </div>
      )}

      <QuickGlance>
        <Card className="p-4 text-sm text-[var(--color-text-secondary)]">
          Showing the at-a-glance summary. Open this page on a larger screen for the full details.
        </Card>
      </QuickGlance>

      {/* Spec 216 T028: the zero-value "Jobs by Type" tile is replaced by the
          collection benchmarks panel (throughput, runtime trends, volume). */}
      <BenchmarksPanel />

      {/* Tabs */}
      <div className="mb-4 flex gap-1 border-b border-[var(--color-border)]">
        {TABS.map((label, i) => (
          <button
            key={label}
            onClick={() => setTabValue(i)}
            className={
              '-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ' +
              (tabValue === i
                ? 'border-[var(--color-accent)] text-[var(--color-accent)]'
                : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]')
            }
          >
            {label}
          </button>
        ))}
      </div>

      {/* Recent Runs */}
      {tabValue === 0 && (
        <Extended>
          {/* Status filter pills */}
          <div className="mb-4 flex flex-wrap gap-2">
            {statusFilters.map(({ label, value }) => {
              const active = filters.status === value;
              return (
                <Button
                  key={label}
                  size="sm"
                  variant={active ? 'primary' : 'outline'}
                  onClick={() => handleStatusFilter(value)}
                >
                  {label}
                </Button>
              );
            })}
          </div>

          <Card padding="none">
            {loading && jobRuns.length === 0 ? (
              <div className="p-4">
                <LoadingState rows={6} />
              </div>
            ) : (
              <DataTable
                columns={runColumns}
                rows={jobRuns}
                rowKey={(j) => j.id}
                minWidth="52rem"
                /* Kept for the mount-time value only. The real guarantee is in
                   DataTable: with showPagination={false} it renders EVERY row given,
                   because the caller owns pagination. Passing pageSize alone could not
                   work - usePagination seeds useState from it and ignores later changes. */
                pageSize={filters.page_size ?? PAGE_SIZES[0]}
                /* 🛑 The server pages, so the table's OWN (client-side) pagination stays
                   suppressed — it would page a slice of a page. The single
                   PaginationControls below is the one control, and it drives the
                   SERVER via handlePageSizeChange.

                   History worth keeping: this screen once carried TWO pagination systems
                   at once — the table footer read "Rows per page: 10" while 20 rows
                   rendered, and a separate pager said "Page 1 of 23". The fix then was to
                   hide the selector, which removed the contradiction by removing the
                   control: users could no longer change page size at all, and a
                   long-running job sat at row 16 of 20 with no way to widen the view.
                   Hiding a control that lies is not the same as making it true. */
                showPagination={false}
                emptyMessage="No job runs match the current filters. Adjust your filters or trigger a new job to get started."
                onRowClick={(j) => handleViewJobDetails(j.id)}
              />
            )}
          </Card>

          {/* Server pagination — the KIT control, driven by the server.
              Blueprint rule (docs/design-blueprint.md): the page-size selector shows
              whenever rows > 0, and navigation only when totalPages > 1.
              PaginationControls implements both, and its own comment says it stays
              visible for exactly this case: "server-paginated tables".

              🛑 `start` is a 0-BASED OFFSET. The control renders `start + 1` ..
              `start + shown` and usePagination feeds it `(page - 1) * pageSize` with
              NO +1. Adding one here double-counted it, and page 1 at size 25 read
              "Showing 2–26 of 25497" on production. */}
          <PaginationControls
            page={page}
            pageSize={filters.page_size ?? PAGE_SIZES[0]}
            total={totalItems}
            totalPages={totalPages}
            start={(page - 1) * (filters.page_size ?? PAGE_SIZES[0])}
            shown={jobRuns.length}
            onPage={handlePageChange}
            onPageSize={handlePageSizeChange}
          />
        </Extended>
      )}

      {/* Job Definitions */}
      {tabValue === 1 && (
        <Extended>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {definitions.map((def) => (
            <Card key={def.id} className="flex flex-col">
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-base font-semibold text-[var(--color-text-primary)]">{def.name}</h3>
                <Badge variant={def.is_enabled ? 'success' : 'secondary'}>
                  {def.is_enabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>
              <p className="mt-1 mb-3 text-sm text-[var(--color-text-secondary)]">
                {def.description || 'No description'}
              </p>
              <div className="mb-3 flex flex-wrap gap-1.5">
                <Badge variant="secondary">{getJobTypeLabel(def.job_type)}</Badge>
                <Badge variant="secondary" isOutlined>
                  {def.timeout_minutes}m timeout
                </Badge>
              </div>
              <div className="mt-auto">
                <RunNowButton
                  enabled={def.is_enabled}
                  runState={jobStates[def.name] ?? null}
                  onClick={() => handleTriggerClick(def)}
                />
              </div>
            </Card>
          ))}
        </div>
        </Extended>
      )}

      {/* Schedules */}
      {tabValue === 2 && (
        <Extended>
          <JobSchedules />
        </Extended>
      )}

      {/* Recent Failures */}
      {summary && summary.recent_failures.length > 0 && (
        <Card className="mt-6 border-[var(--color-status-warning)]/40 bg-[var(--color-status-warning)]/5">
          <div className="flex items-center gap-2 text-[var(--color-status-warning)]">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            <span className="text-sm font-semibold">Recent Failures</span>
          </div>
          <ul className="mt-2 list-disc space-y-1 pl-6 text-sm text-[var(--color-text-secondary)]">
            {summary.recent_failures.slice(0, 3).map((failure) => (
              <li key={failure.id}>
                {failure.job_definition?.name || 'Unknown Job'} —{' '}
                {failure.error_message || 'No error message'}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Trigger Job Dialog (own modal — unchanged) */}
      <TriggerJobDialog
        open={triggerDialogOpen}
        onClose={() => setTriggerDialogOpen(false)}
        onSuccess={handleTriggerSuccess}
        jobDefinition={selectedDefinition}
      />

      {/* Job Detail Modal (own modal — unchanged) */}
      <JobDetail
        open={detailDialogOpen}
        jobRunId={selectedJobRunId}
        onClose={() => setDetailDialogOpen(false)}
      />

      {/* Success toast (token-styled; replaces MUI <Snackbar>) */}
      {successSnackbar && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2">
          <div className="flex items-center gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] px-4 py-3 text-sm text-[var(--color-text-primary)] shadow-2xl">
            <CheckCircle className="h-4 w-4 text-[var(--color-status-success)]" />
            <span>{successMessage}</span>
            <button
              onClick={() => setSuccessSnackbar(false)}
              aria-label="Dismiss"
              className="text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-text-primary)]"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </PageLayout>
  );
}
